import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { businessDate, money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';

export type SourceKind = 'salesInvoice' | 'supplierInvoice' | 'goodsReceipt';

export interface SourceOption {
  id: string;
  number: string;
  party: string;
  date: string;
  amount: string | null;
  hint: string | null;
}

const LABELS: Record<SourceKind, string> = {
  salesInvoice: 'Invoice',
  supplierInvoice: 'Bill',
  goodsReceipt: 'Goods receipt',
};

async function search(kind: SourceKind, q: string): Promise<SourceOption[]> {
  const query = { q: q || undefined, pageSize: 20, status: 'POSTED' as const };
  if (kind === 'salesInvoice') {
    const r = await unwrap(api.GET('/sales-invoices', { params: { query } }));
    return r.data.map((i) => ({
      id: i.id,
      number: i.number,
      party: i.contact.name,
      date: i.docDate,
      amount: i.total,
      hint: i.paymentStatus,
    }));
  }
  if (kind === 'supplierInvoice') {
    const r = await unwrap(api.GET('/supplier-invoices', { params: { query } }));
    return r.data.map((i) => ({
      id: i.id,
      number: i.number,
      party: i.contact.name,
      date: i.docDate,
      amount: i.total,
      hint: i.supplierInvoiceNumber,
    }));
  }
  const r = await unwrap(api.GET('/goods-receipts', { params: { query } }));
  return r.data.map((g) => ({
    id: g.id,
    number: g.number,
    party: g.contact.name,
    date: g.docDate,
    amount: g.totalCost,
    hint: g.supplierRef,
  }));
}

/** Searchable picker of the posted document a return or note is made against. */
export function SourcePicker({
  kind,
  onPick,
  autoFocus,
}: {
  kind: SourceKind;
  onPick: (option: SourceOption) => void;
  autoFocus?: boolean;
}) {
  const { format } = useShop();
  const [q, setQ] = useState('');
  const { data, isFetching } = useQuery({
    queryKey: ['returns', 'source-picker', kind, q],
    queryFn: () => search(kind, q),
    placeholderData: keepPreviousData,
  });
  return (
    <Autocomplete<SourceOption>
      options={data ?? []}
      loading={isFetching}
      filterOptions={(x) => x}
      onInputChange={(_, v, reason) => reason === 'input' && setQ(v)}
      onChange={(_, v) => v && onPick(v)}
      getOptionLabel={(o) => `${o.number} · ${o.party}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={q ? 'No posted documents match' : 'No posted documents yet'}
      renderOption={({ key, ...props }, o) => (
        <Box component="li" key={key} {...props}>
          <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                {o.number}
                {o.hint && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    · {o.hint.replace(/_/g, ' ').toLowerCase()}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {o.party} · {businessDate(o.date, format)}
              </Typography>
            </Box>
            {o.amount && (
              <Typography variant="body2" className="tabular" sx={{ fontWeight: 600 }}>
                {money(o.amount, format)}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={`Find the ${LABELS[kind].toLowerCase()}`}
          placeholder="Number or party name"
        />
      )}
    />
  );
}
